import { 
  Component, 
  ContentChildren, 
  QueryList, 
  AfterContentInit, 
  Input, 
  Output, 
  EventEmitter, 
  ViewChild, 
  TemplateRef,
  signal,
  computed,
  OnDestroy,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { CustomStepComponent } from './custom-step.component';

export interface StepperSelectionEvent {
  selectedIndex: number;
  selectedStep: CustomStepComponent;
  previouslySelectedIndex: number;
  previouslySelectedStep: CustomStepComponent | null;
}

@Component({
  selector: 'custom-stepper',
  standalone: true,
  imports: [CommonModule],
  animations: [
    // Enhanced animation with scale / opacity / directional slide
    trigger('stepTransition', [
      transition('* => *', [
        style({
          opacity: '{{fromOpacity}}',
          transform: 'translateX({{startX}}%) scale({{startScale}})'
        }),
        animate(
          '{{duration}}ms {{easing}}',
          style({ opacity: 1, transform: 'translateX(0) scale(1)' })
        )
      ], { params: { duration: 450, startX: 0, startScale: 1, easing: 'ease', fromOpacity: 0 } })
    ])
  ],
  template: `
    <div class="custom-stepper" [class.linear]="linear">
      <div class="stepper-content" [class.animating]="isAnimating()">
  <div class="step-wrapper" *ngIf="selectedStep() as activeStep" [@stepTransition]="{ value: selectedIndex(), params: animationParams() }">
          <ng-container 
            [ngTemplateOutlet]="activeStep.content"
            [ngTemplateOutletContext]="{ $implicit: activeStep, index: selectedIndex() }">
          </ng-container>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./custom-stepper.component.scss']
})
export class CustomStepperComponent implements AfterContentInit, OnDestroy {
  @ContentChildren(CustomStepComponent) steps!: QueryList<CustomStepComponent>;
  
  @Input() selectedIndex = signal(0);
  @Input() linear: boolean = false;
  @Input() animationDuration: number = 450; // slowed a bit
  /** Animation style */
  @Input() animationType: 'slide' | 'fade' | 'scale' | 'slideScale' | 'crossFade' | 'elegant' = 'slide';
  /** Custom easing function */
  @Input() animationEasing: string = 'cubic-bezier(0.22,0.61,0.36,1)';
  /** Disable animations if desired */
  @Input() disableAnimations: boolean = false;
  
  @Output() selectionChange = new EventEmitter<StepperSelectionEvent>();
  @Output() stepChange = new EventEmitter<StepperSelectionEvent>();
  
  private cdr = inject(ChangeDetectorRef);
  
  readonly selectedStep = computed(() => {
    const index = this.selectedIndex();
    const stepsArray = this.steps?.toArray() || [];
    return stepsArray[index] || null;
  });
  
  readonly isFirstStep = computed(() => this.selectedIndex() === 0);
  readonly isLastStep = computed(() => {
    const stepsArray = this.steps?.toArray() || [];
    return this.selectedIndex() === stepsArray.length - 1;
  });
  
  readonly canGoNext = computed(() => {
    if (this.isLastStep()) return false;
    
    if (!this.linear) return true;
    
    const currentStep = this.selectedStep();
    return currentStep ? !currentStep.hasError && (currentStep.completed || currentStep.optional) : false;
  });
  
  readonly canGoPrevious = computed(() => {
    if (this.isFirstStep()) return false;
    return true;
  });
  
  ngAfterContentInit(): void {
    this.updateStepStates();
    
    // Listen for changes in steps
    this.steps.changes.subscribe(() => {
      this.updateStepStates();
      this.cdr.detectChanges();
    });
  }
  
  ngOnDestroy(): void {
    // Cleanup if needed
  }
  
  // Track previous index for directional animation
  private _previousIndex = 0;
  private _isAnimating = signal(false);

  isAnimating = this._isAnimating.asReadonly();

  animationParams = () => {
    if (this.disableAnimations) {
      return { duration: 0, startX: 0, startScale: 1, easing: 'linear', fromOpacity: 1 };
    }
    const forward = this.selectedIndex() >= this._previousIndex;
    const dir = forward ? 1 : -1;
    const base = { duration: this.animationDuration, easing: this.animationEasing } as any;
    switch (this.animationType) {
      case 'fade':
        return { ...base, startX: 0, startScale: 1, fromOpacity: 0 };
      case 'scale':
        return { ...base, startX: 0, startScale: 0.94, fromOpacity: 0 };
      case 'slideScale':
        return { ...base, startX: 12 * dir, startScale: 0.96, fromOpacity: 0 };
      case 'crossFade':
        return { ...base, startX: 4 * dir, startScale: 1, fromOpacity: 0 };
      case 'elegant':
        return { ...base, startX: 18 * dir, startScale: 0.97, fromOpacity: 0 };
      default: // slide
        return { ...base, startX: 15 * dir, startScale: 1, fromOpacity: 0 };
    }
  };

  private updateStepStates(): void {
    const stepsArray = this.steps.toArray();
    const currentIndex = this.selectedIndex();
    
    stepsArray.forEach((step, index) => {
      step._stepIndex = index;
      step._isActive = index === currentIndex;
      
      // Mark steps as visited if they are before current step or if stepper is not linear
      if (!this.linear || index < currentIndex) {
        step.markAsVisited();
      }
    });
  }
  
  /**
   * Navigate to the next step
   */
  next(): void {
    if (this.canGoNext()) {
      this.goToStep(this.selectedIndex() + 1);
    }
  }
  
  /**
   * Navigate to the previous step
   */
  previous(): void {
    if (this.canGoPrevious()) {
      this.goToStep(this.selectedIndex() - 1);
    }
  }
  
  /**
   * Navigate to a specific step by index
   */
  goToStep(index: number): void {
    const stepsArray = this.steps.toArray();
    
    if (index < 0 || index >= stepsArray.length) {
      return;
    }
    
    // Check if we can navigate to this step (in linear mode)
    if (this.linear && index > this.selectedIndex()) {
      // Can only go forward if current step is valid
      const currentStep = this.selectedStep();
      if (currentStep && (currentStep.hasError || (!currentStep.completed && !currentStep.optional))) {
        return;
      }
    }
    
  const previousIndex = this.selectedIndex();
    const previousStep = this.selectedStep();
    const newStep = stepsArray[index];
    
    // Update selection
  this._previousIndex = previousIndex;
  this._isAnimating.set(true);
    this.selectedIndex.set(index);
    this.updateStepStates();
    
    // Emit events
    const event: StepperSelectionEvent = {
      selectedIndex: index,
      selectedStep: newStep,
      previouslySelectedIndex: previousIndex,
      previouslySelectedStep: previousStep
    };
    
  this.selectionChange.emit(event);
  this.stepChange.emit(event);
    
  // Clear animation flag after duration
  const duration = this.disableAnimations ? 0 : this.animationDuration;
  setTimeout(() => this._isAnimating.set(false), duration);
  this.cdr.detectChanges();
  }
  
  /**
   * Navigate to a step by ID
   */
  goToStepById(stepId: string): void {
    const stepsArray = this.steps.toArray();
    const stepIndex = stepsArray.findIndex(step => step.stepId === stepId);
    
    if (stepIndex !== -1) {
      this.goToStep(stepIndex);
    }
  }
  
  /**
   * Get step by index
   */
  getStep(index: number): CustomStepComponent | null {
    const stepsArray = this.steps.toArray();
    return stepsArray[index] || null;
  }
  
  /**
   * Get step by ID
   */
  getStepById(stepId: string): CustomStepComponent | null {
    const stepsArray = this.steps.toArray();
    return stepsArray.find(step => step.stepId === stepId) || null;
  }
  
  /**
   * Get all steps
   */
  getAllSteps(): CustomStepComponent[] {
    return this.steps.toArray();
  }
  
  /**
   * Get current step count
   */
  getStepCount(): number {
    return this.steps.length;
  }
  
  /**
   * Reset all steps
   */
  reset(): void {
    this.steps.toArray().forEach(step => step.reset());
    this.selectedIndex.set(0);
    this.updateStepStates();
  }
  
  /**
   * Check if a specific step is completed
   */
  isStepCompleted(index: number): boolean {
    const step = this.getStep(index);
    return step ? step.completed : false;
  }
  
  /**
   * Check if a specific step has errors
   */
  isStepInvalid(index: number): boolean {
    const step = this.getStep(index);
    return step ? step.hasError : false;
  }
  
  /**
   * Mark a step as completed
   */
  markStepCompleted(index: number, completed: boolean = true): void {
    const step = this.getStep(index);
    if (step) {
      step.completed = completed;
      this.updateStepStates();
    }
  }
  
  /**
   * Mark a step as having errors
   */
  markStepInvalid(index: number, hasError: boolean = true): void {
    const step = this.getStep(index);
    if (step) {
      step.hasError = hasError;
      this.updateStepStates();
    }
  }
}
